import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
// Note: Heroicons imports removed - using text-based icons for now
// TODO: Install @heroicons/react package or use alternative icons

import { 
  Button, 
  Input, 
  Card, 
  Badge,
  ExpenseTable,
  ConfirmationDialog
} from '../components';
import { useToast } from '../components/ui/Toast';
import { useAuth } from '../components/auth/AuthProvider';
import { exportExpensesToCSV, exportExpensesToExcel, exportExpensesToPDF } from '../utils/export';
import { useApiClient } from '../hooks/useApiClient';

// Simple inline spinner for loading indicators
const Spinner: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg
    className="animate-spin text-blue-600"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    width={size}
    height={size}
    aria-hidden="true"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
    />
  </svg>
);

// Types
interface Expense {
  id: string;
  amount: number;
  currency: string;
  description: string;
  expense_date: string;
  merchant_name?: string;
  status: 'draft' | 'submitted' | 'pending_approval' | 'approved' | 'rejected' | 'paid' | 'cancelled';
  category: {
    id: string;
    name: string;
  };
  user: {
    id: string;
    name: string;
    email: string;
  };
  created_at: string;
  updated_at: string;
}

interface ExpenseFilters {
  status?: string[];
  category_id?: string;
  start_date?: string;
  end_date?: string;
  min_amount?: number;
  max_amount?: number;
  search?: string;
  currency?: string;
  is_billable?: boolean;
  project_code?: string;
}

interface ExpenseCategory {
  id: string;
  name: string;
  code: string;
}

const ExpensesList: React.FC = () => {
  // Feature flags
  const ENABLE_BULK_ACTIONS = false;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  const toastRef = useRef(showToast);
  useEffect(() => { toastRef.current = showToast; }, [showToast]);
  const { isAuthenticated } = useAuth();
  const apiClient = useApiClient();

  // State
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [loading, setLoading] = useState(true);
  // UX-only loading that persists briefly to make state change obvious
  const [visualLoading, setVisualLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const initializedSetRef = useRef(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedExpenses, setSelectedExpenses] = useState<string[]>([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState<string | null>(null);
  // Which status button was clicked (for per-button feedback)
  const [pressedStatus, setPressedStatus] = useState<string | null>(null);

  // Filters - Initialize once to prevent recreation on every render
  const [filters, setFilters] = useState<ExpenseFilters>(() => ({
    search: searchParams.get('search') || '',
    status: (() => {
      const raw = searchParams.get('status') || '';
      return raw ? raw.split(',').filter(Boolean) : [];
    })(),
    category_id: searchParams.get('category') || '',
    start_date: searchParams.get('start_date') || '',
    end_date: searchParams.get('end_date') || '',
    min_amount: searchParams.get('min_amount') ? Number(searchParams.get('min_amount')) : undefined,
    max_amount: searchParams.get('max_amount') ? Number(searchParams.get('max_amount')) : undefined,
    currency: searchParams.get('currency') || '',
    is_billable: searchParams.get('is_billable') === 'true' ? true : (searchParams.get('is_billable') === 'false' ? false : undefined),
    project_code: searchParams.get('project_code') || '',
  }));

  // Status options
  const statusOptions = [
    { value: 'draft', label: 'Draft', color: 'gray' },
    { value: 'submitted', label: 'Submitted', color: 'blue' },
    { value: 'pending_approval', label: 'Pending Approval', color: 'yellow' },
    { value: 'approved', label: 'Approved', color: 'green' },
    { value: 'rejected', label: 'Rejected', color: 'red' },
    { value: 'paid', label: 'Paid', color: 'purple' },
    { value: 'cancelled', label: 'Cancelled', color: 'gray' },
  ];

  // Sorting controls aligned with backend ExpenseSortBy
  const sortOptions = [
    { value: 'created_at', label: 'Created At' },
    { value: 'updated_at', label: 'Updated At' },
    { value: 'expense_date', label: 'Expense Date' },
    { value: 'amount', label: 'Amount' },
    { value: 'status', label: 'Status' },
    { value: 'description', label: 'Description' },
    { value: 'merchant_name', label: 'Merchant' },
  ];

  const [sortBy, setSortBy] = useState<string>(searchParams.get('sort_by') || 'created_at');
  const [sortDesc, setSortDesc] = useState<boolean>(searchParams.get('sort_desc') ? searchParams.get('sort_desc') === 'true' : true);

  // Memoized key for status list to use safely in dependencies
  const statusKey = useMemo(() => (filters.status || []).join(','), [filters.status]);

  // Single-flight guard to prevent overlapping requests
  const isFetchingRef = useRef(false);
  // Track pending reload if a filter changes during an in-flight request
  const pendingReloadRef = useRef(false);

  // Debounced search input
  const [searchInput, setSearchInput] = useState<string>(filters.search || '');
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: searchInput }));
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Validate and clamp date/amount ranges when filters change
  useEffect(() => {
    // Date range validation: end_date >= start_date
    if (filters.start_date && filters.end_date && filters.end_date < filters.start_date) {
      toastRef.current('End date must be on or after start date', 'error');
      setFilters(prev => ({ ...prev, end_date: '' }));
    }
    // Amount range validation
    if (
      typeof filters.min_amount === 'number' &&
      typeof filters.max_amount === 'number' &&
      filters.max_amount <= filters.min_amount
    ) {
      toastRef.current('Max amount must be greater than min amount', 'error');
      setFilters(prev => ({ ...prev, max_amount: undefined }));
    }
  }, [filters.start_date, filters.end_date, filters.min_amount, filters.max_amount]);

  // Load expenses
  const loadExpenses = useCallback(async () => {
    try {
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;
      setLoading(true);
    
      // Build query parameters
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('size', pageSize.toString());
      params.append('sort_by', sortBy);
      params.append('sort_desc', String(sortDesc));
      
      if (filters.search) params.append('search', filters.search);
      if (filters.status?.length) {
        params.set('status', statusKey);
      }
      if (filters.category_id) params.append('category_id', filters.category_id);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.min_amount) params.append('min_amount', filters.min_amount.toString());
      if (filters.max_amount) params.append('max_amount', filters.max_amount.toString());
      if (filters.currency) params.append('currency', String(filters.currency).toUpperCase());
      if (typeof filters.is_billable === 'boolean') params.append('is_billable', String(filters.is_billable));
      if (filters.project_code) params.append('project_code', filters.project_code);

      const data = await apiClient.get<{items: Expense[], total: number}>(`/expenses/?${params}`);

      // apiClient.get() returns data directly, no need to check response.ok or call .json()
      setExpenses(data.items);
      setTotalCount(data.total);
      // Mark initialized only once on successful load to prevent premature "No expenses" state
      if (!initializedSetRef.current) {
        initializedSetRef.current = true;
        setInitialized(true);
      }
    } catch (error) {
      toastRef.current('Failed to load expenses', 'error');
      console.error('Error loading expenses:', error);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
      if (pendingReloadRef.current) {
        // a change arrived while fetching; consume the flag and immediately refetch
        pendingReloadRef.current = false;
        // fire and forget; effect deps already reflect latest filters
        // avoid infinite loop by checking guard again
        if (!isFetchingRef.current) {
          isFetchingRef.current = true;
          // call through microtask to allow state to settle
          Promise.resolve().then(() => {
            setLoading(true);
            // delegate to the same function
            loadExpenses();
          });
        }
      }
    }
  }, [apiClient, currentPage, pageSize, filters, statusKey, sortBy, sortDesc]);

  // Manage visual loading to avoid flicker in the header progress bar only
  useEffect(() => {
    let showTimer: any;
    if (loading) {
      // show after a small delay to prevent flashing on ultra-fast responses
      showTimer = setTimeout(() => setVisualLoading(true), 80);
    } else {
      // remove immediately so table can render as soon as data arrives
      setVisualLoading(false);
    }
    return () => {
      if (showTimer) clearTimeout(showTimer);
    };
  }, [loading]);

  // Clear pressed status once visual loading ends
  useEffect(() => {
    if (!visualLoading && pressedStatus) {
      setPressedStatus(null);
    }
  }, [visualLoading, pressedStatus]);

  // Load categories
  const loadCategories = useCallback(async () => {
    try {
      const data = await apiClient.get<ExpenseCategory[]>('/expenses/categories/');
      setCategories(data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  }, [apiClient]);

  

  useEffect(() => {
    if (!isAuthenticated) return;
    loadExpenses();
  }, [isAuthenticated, loadExpenses]);

  const categoriesLoadedRef = useRef(false);
  useEffect(() => {
    if (!isAuthenticated) return;
    if (categoriesLoadedRef.current) return; // guard duplicate under StrictMode
    categoriesLoadedRef.current = true;
    loadCategories();
  }, [isAuthenticated, loadCategories]);

  // Update URL params when filters change (only if different)
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.search) params.set('search', filters.search);
    if (filters.status?.length) params.set('status', statusKey);
    if (filters.category_id) params.set('category', filters.category_id);
    if (filters.start_date) params.set('start_date', filters.start_date);
    if (filters.end_date) params.set('end_date', filters.end_date);
    if (typeof filters.min_amount === 'number') params.set('min_amount', String(filters.min_amount));
    if (typeof filters.max_amount === 'number') params.set('max_amount', String(filters.max_amount));
    if (filters.currency) params.set('currency', String(filters.currency).toUpperCase());
    if (typeof filters.is_billable === 'boolean') params.set('is_billable', String(filters.is_billable));
    if (filters.project_code) params.set('project_code', filters.project_code);
    params.set('sort_by', sortBy);
    params.set('sort_desc', String(sortDesc));

    const next = params.toString();
    const current = searchParams.toString();
    if (next !== current) {
      setSearchParams(params, { replace: true });
    }
  }, [
    filters.search,
    filters.status?.length,
    statusKey,
    filters.category_id,
    filters.start_date,
    filters.end_date,
    filters.min_amount,
    filters.max_amount,
    filters.currency,
    filters.is_billable,
    filters.project_code,
    sortBy,
    sortDesc,
    searchParams,
    setSearchParams
  ]);

  // Handlers
  const handleSearch = (value: string) => {
    setSearchInput(value);
  };

  const handleFilterChange = (key: keyof ExpenseFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
    // If a fetch is in-flight, mark a pending reload so we don't drop this change
    if (isFetchingRef.current) {
      pendingReloadRef.current = true;
    }
  };

  const handleStatusFilter = (status: string) => {
    const currentStatuses = filters.status || [];
    const newStatuses = currentStatuses.includes(status)
      ? currentStatuses.filter(s => s !== status)
      : [...currentStatuses, status];
    
    // Immediate visual feedback
    setVisualLoading(true);
    setPressedStatus(status);
    handleFilterChange('status', newStatuses);
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status: [],
      category_id: '',
      start_date: '',
      end_date: '',
    });
    setCurrentPage(1);
  };

  const handleExpenseClick = (expense: Expense) => {
    navigate(`/expenses/${expense.id}`);
  };

  const handleDeleteExpense = async (expenseId: string) => {
    try {
      await apiClient.delete(`/expenses/${expenseId}`);

      showToast('Expense deleted successfully', 'success');
      loadExpenses();
    } catch (error) {
      showToast('Failed to delete expense', 'error');
      console.error('Error deleting expense:', error);
    }
  };

  const handleBulkAction = async (action: 'approve' | 'reject' | 'delete') => {
    try {
      if (!selectedExpenses.length) {
        showToast('Select at least one expense first', 'info');
        return;
      }
      if (action === 'delete') {
        const ok = window.confirm(`Delete ${selectedExpenses.length} selected expense(s)? This cannot be undone.`);
        if (!ok) return;
      }
      setVisualLoading(true);
      const payload: any = {
        expense_ids: selectedExpenses,
        action,
      };
      if (action === 'reject') {
        payload.comments = 'Rejected via bulk action';
      }
      await apiClient.post('/expenses/bulk-action', payload);
      showToast(`Bulk ${action} completed`, 'success');
      setSelectedExpenses([]);
      loadExpenses();
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 501) {
        showToast('Bulk action not yet available on server', 'info');
      } else {
        showToast(`Bulk ${action} failed`, 'error');
      }
      console.error('Bulk action error:', error);
    }
  };

  const handleExport = async (format: 'csv' | 'pdf' | 'excel') => {
    if (!expenses.length) {
      showToast('No expenses to export', 'info');
      return;
    }
    const filenameBase = `expenses_${new Date().toISOString().slice(0,10)}`;
    try {
      if (format === 'csv') {
        exportExpensesToCSV(expenses as any, `${filenameBase}.csv`);
      } else if (format === 'excel') {
        exportExpensesToExcel(expenses as any, `${filenameBase}.xls`);
      } else if (format === 'pdf') {
        exportExpensesToPDF(expenses as any, `${filenameBase}.pdf`);
      }
    } catch (err) {
      console.error('Export failed:', err);
      showToast('Failed to export expenses', 'error');
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 -m-4 sm:-m-6 lg:-m-8 mb-6 px-4 sm:px-6 lg:px-8 py-4" aria-busy={visualLoading}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Expenses</h1>
            <p className="text-sm text-gray-600 mt-1">{totalCount} total expenses</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2"
            >
              🔽
              Filters
              {(filters.status?.length || filters.category_id || filters.start_date) && (
                <Badge variant="secondary" size="sm">
                  {(filters.status?.length || 0) + (filters.category_id ? 1 : 0) + (filters.start_date ? 1 : 0)}
                </Badge>
              )}
            </Button>
            
            {ENABLE_BULK_ACTIONS && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowBulkActions(!showBulkActions)}
                className="flex items-center gap-2"
              >
                ⋯
                Actions
              </Button>
            )}

            <Button
              onClick={() => navigate('/expenses/create')}
              className="flex items-center gap-2"
            >
              +
              New Expense
            </Button>
          </div>
        </div>
        {visualLoading && (
          <div className="mt-3 h-1 w-full bg-blue-100">
            <div className="h-1 w-1/3 bg-blue-600 animate-pulse rounded" />
          </div>
        )}
      </div>

      {/* Search */}
        <Card className="p-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search expenses..."
                value={searchInput}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full"
                leftIcon={<span>🔍</span>}
              />
            </div>
            
            {/* Quick status filters */}
            <div className="flex items-center gap-2" aria-live="polite">
              {statusOptions.slice(0, 4).map((status) => (
                <Button
                  key={status.value}
                  variant={filters.status?.includes(status.value) ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleStatusFilter(status.value)}
                  disabled={visualLoading}
                >
                  <span className="inline-flex items-center gap-2">
                    {status.label}
                    {pressedStatus === status.value && visualLoading && <Spinner size={12} />}
                  </span>
                </Button>
              ))}
              {visualLoading && (
                <span className="inline-flex items-center gap-2 text-sm text-gray-600">
                  <Spinner />
                  Applying filters...
                </span>
              )}
            </div>
            {/* Active status chips for clarity */}
            {filters.status && filters.status.length > 0 && (
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-600">
                <span>Filtering by:</span>
                {filters.status.map(s => {
                  const opt = statusOptions.find(o => o.value === s);
                  return (
                    <span key={s} className="px-2 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                      {opt?.label || s}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        </Card>

        {/* pressedStatus cleared via effect below */}

        {/* Advanced Filters */}
        {showFilters && (
          <Card className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <select
                  value={filters.category_id || ''}
                  onChange={(e) => handleFilterChange('category_id', e.target.value)}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                >
                  <option value="">All Categories</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date
                </label>
                <Input
                  type="date"
                  value={filters.start_date || ''}
                  onChange={(e) => handleFilterChange('start_date', e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date
                </label>
                <Input
                  type="date"
                  value={filters.end_date || ''}
                  onChange={(e) => handleFilterChange('end_date', e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Min Amount</label>
                <Input
                  type="number"
                  value={typeof filters.min_amount === 'number' ? filters.min_amount : ''}
                  onChange={(e) => handleFilterChange('min_amount', e.target.value ? Number(e.target.value) : undefined)}
                  placeholder="e.g., 10"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Max Amount</label>
                <Input
                  type="number"
                  value={typeof filters.max_amount === 'number' ? filters.max_amount : ''}
                  onChange={(e) => handleFilterChange('max_amount', e.target.value ? Number(e.target.value) : undefined)}
                  placeholder="e.g., 500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
                <select
                  value={filters.currency || ''}
                  onChange={(e) => handleFilterChange('currency', e.target.value)}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                >
                  <option value="">All</option>
                  {['USD','EUR','GBP','CAD','AUD','JPY','CHF','CNY','INR'].map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2 mt-6">
                <input
                  id="is_billable"
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                  checked={filters.is_billable === true}
                  onChange={(e) => handleFilterChange('is_billable', e.target.checked ? true : undefined)}
                />
                <label htmlFor="is_billable" className="text-sm text-gray-700">Billable only</label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Project Code</label>
                <Input
                  value={filters.project_code || ''}
                  onChange={(e) => handleFilterChange('project_code', e.target.value)}
                  placeholder="e.g., PROJ-123"
                />
              </div>

              <div className="flex items-end">
                <Button
                  variant="outline"
                  onClick={clearFilters}
                  className="w-full"
                >
                  Clear Filters
                </Button>
              </div>
            </div>

            {/* Sort controls */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                >
                  {sortOptions.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <Button
                  variant="outline"
                  onClick={() => setSortDesc(prev => !prev)}
                  className="w-full"
                >
                  {sortDesc ? 'Descending' : 'Ascending'}
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Bulk Actions (hidden via feature flag) */}
        {ENABLE_BULK_ACTIONS && showBulkActions && selectedExpenses.length > 0 && (
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">
                {selectedExpenses.length} expense(s) selected
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkAction('approve')}
                >
                  Approve Selected
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkAction('reject')}
                >
                  Reject Selected
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleBulkAction('delete')}
                >
                  Delete Selected
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Empty state vs. Export + Table */}
        {initialized && !loading && totalCount === 0 ? (
          <Card className="p-10 text-center">
            <div className="max-w-xl mx-auto space-y-4">
              <div className="mx-auto w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 text-2xl">🧾</div>
              <h2 className="text-xl font-semibold text-gray-900">No expenses yet</h2>
              <p className="text-gray-600">
                You don’t have any expenses. Create your first expense to get started.
              </p>
              <div className="pt-2">
                <Button onClick={() => navigate('/expenses/create')} className="">+ Create your first expense</Button>
              </div>
            </div>
          </Card>
        ) : (
          <>
            {/* Export Actions */}
            <Card className="p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">
                  Export Options
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleExport('csv')}
                    disabled={!expenses.length}
                    className="flex items-center gap-2"
                  >
                    ⬇
                    CSV
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleExport('excel')}
                    disabled={!expenses.length}
                    className="flex items-center gap-2"
                  >
                    ⬇
                    Excel
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleExport('pdf')}
                    disabled={!expenses.length}
                    className="flex items-center gap-2"
                  >
                    ⬇
                    PDF
                  </Button>
                </div>
              </div>
            </Card>

            {/* Expenses Table (dim during loading, but keep clickable) */}
            <Card className={visualLoading ? "opacity-95 transition-opacity" : "transition-opacity"}>
              <ExpenseTable
                expenses={expenses}
                loading={loading}
                initialized={initialized}
                onExpenseClick={handleExpenseClick}
                selectable={ENABLE_BULK_ACTIONS && showBulkActions}
                selectedIds={selectedExpenses}
                onSelectionChange={setSelectedExpenses}
                showActions={true}
              />
            </Card>
          </>
        )}

        {/* Pagination */}
        {totalCount > pageSize && (
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">
                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} expenses
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  Previous
                </Button>
                <span className="px-3 py-1 text-sm">
                  Page {currentPage} of {Math.ceil(totalCount / pageSize)}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage >= Math.ceil(totalCount / pageSize)}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </Card>
        )}
        
        {/* Delete Confirmation Dialog */}
        <ConfirmationDialog
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={() => {
            if (expenseToDelete) {
              handleDeleteExpense(expenseToDelete);
              setShowDeleteConfirm(false);
              setExpenseToDelete(null);
            }
          }}
          title="Delete Expense"
          description="Are you sure you want to delete this expense? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          variant="destructive"
        />
    </div>
  );
};

export default ExpensesList;
